import * as PromisePool from 'es6-promise-pool';
import {MyWindow} from '../../types/MyWindow';
import Config from '../../globals/Config';
import Log from '../../utils/Log';
import Strings from '../../globals/Strings';
import Store from '../../redux/Store';
import Actions from '../../redux/Actions';
import UserService from '../../services/UserService/UserService';
import CancellationToken from '../../utils/CancellationToken';
import WebConnectorUserInfo from '../../types/WebConnectorUserInfo';
import UserMessaging from '../../utils/UserMessaging';
import EntitlementService from '../EntitlementService/EntitlementService';
import {UserTypes} from '../../globals/Constants';

declare const window: MyWindow;
let hasLoggedInThisSession = false;
let hasShownInfoChangedWarning = false;

const webConnectorModuleConfig = {debug: Config.webConnector.debug};

const onError = (error: string): void => {
   Log.warn(error);
   throw error;
};

const webConnector = window.tscwc.create(Config.webConnector);
const authModule: any = webConnector.hasModule('auth', webConnectorModuleConfig);

const ensureLogInComplete = (): Promise<any> => {
   return authModule.ensureLogInComplete().catch(onError);
};

const oauthModule: any = webConnector.hasModule('oauth', webConnectorModuleConfig);
const storageModulePromise: Promise<any> = ensureLogInComplete().then(() => webConnector.hasModule('storage', webConnectorModuleConfig));
const resolverModulePromise: Promise<any> = ensureLogInComplete().then(() => webConnector.hasModule('resolver', webConnectorModuleConfig));

const getUserInfo = (): Promise<any> => {
   return authModule.getAuthInfo().catch(onError);
};

const warnUserInfoHasChanged = () => {
   if (!hasShownInfoChangedWarning) {
      hasShownInfoChangedWarning = true;
      UserMessaging.warn(Strings.errors.userInfoChanged);
   }
};

window.addEventListener('tsc_auth_change', () => {
   warnUserInfoHasChanged();
   onUserInfoChange();
});

const onUserInfoChange = async (): Promise<void> => {
   const {id, profileImageUrl, displayName, email}: WebConnectorUserInfo = await getUserInfo();
   Store.dispatch(Actions.setUserId(id));
   UserService.setUsers([{Id: id, IsUserDeleted: false, UserType: UserTypes.techSmith, DisplayName: displayName, EmailAddress: email, ProfileImageUrl: profileImageUrl}]);
   await EntitlementService.getEntitlementStatus(id);
};

const saveUserInfoToStore = async (): Promise<void> => {
   const {id}: WebConnectorUserInfo = await getUserInfo();
   Store.dispatch(Actions.setUserId(id));
   UserService.setUserInfo({
      id: id,
      profileImageUrl: Config.user.profileImageUrl,
      email: Config.user.email,
      displayName: Config.user.displayName
   });

   await EntitlementService.getEntitlementStatus(id);
};

const login = async (): Promise<void> => {
   if (hasLoggedInThisSession) {
      return;
   }

   hasLoggedInThisSession = true;
   try {
      await oauthModule.login({prompt: false});
   } catch (e) {
      onError(e);
   }
};

const loginAndSave = async (): Promise<void> => {
   if (hasLoggedInThisSession) {
      return;
   }

   await login();
   await saveUserInfoToStore();
};

const errorIsTimeout = (error: any) => {
   return error && error.status === 0;
};

const getToken = async (): Promise<string> => {
   try {
      await ensureLogInComplete();
      return await oauthModule.getToken();
   } catch (e) {
      if (!errorIsTimeout(e)) {
         warnUserInfoHasChanged();
      }

      onError(e);
   }
};

const refreshToken = async (): Promise<string> => {
   try {
      await ensureLogInComplete();
      return await oauthModule.refreshToken();
   } catch (e) {
      if (!errorIsTimeout(e)) {
         warnUserInfoHasChanged();
      }

      onError(e);
   }
};

const createThumbnail = async (identifier: string): Promise<void> => {
   try {
      const storage = await storageModulePromise;
      return await storage.getThumbnailUrl(identifier, true);
   } catch (e) {
      onError(e);
   }
};

const upload = async (file: File, cancellationToken: CancellationToken, onProgress: (progress: number) => void): Promise<string> => {
   try {
      const storage = await storageModulePromise;
      const uploadId: string = await storage.multiPartCreateFile(file.name);
      if (cancellationToken.isCancellationRequested) {
         throw new Error(Strings.errors.uploadCancelled);
      }

      const concurrentChunkUploads = Config.concurrentChunkUploads;
      const chunkSize = Config.uploadChunkSize;

      let completedPromises = 0;
      const totalNumberOfChunks = Math.ceil(file.size / chunkSize);
      let numberOfChunksSent = 0;

      const promiseProducer = (): Promise<void> | null => {
         if (cancellationToken.isCancellationRequested) {
            throw new Error(Strings.errors.uploadCancelled);
         }

         if (numberOfChunksSent >= totalNumberOfChunks) {
            return null;
         }

         const startIndex = numberOfChunksSent * chunkSize;
         const endIndex = startIndex + chunkSize;

         return storage.multiPartAddChunkToFile(uploadId, numberOfChunksSent++, file.slice(startIndex, Math.min(endIndex, file.size))).then(() => {
            completedPromises++;
            if (onProgress && !cancellationToken.isCancellationRequested) {
               onProgress(completedPromises / totalNumberOfChunks);
            }
         });
      };

      const promisePool = new PromisePool(promiseProducer, concurrentChunkUploads);
      await promisePool.start();

      if (cancellationToken.isCancellationRequested) {
         throw new Error(Strings.errors.uploadCancelled);
      }
      await storage.multiPartCompleteFile(uploadId);
      if (cancellationToken.isCancellationRequested) {
         throw new Error(Strings.errors.uploadCancelled);
      }
      createThumbnail(uploadId);
      return uploadId;
   } catch (e) {
      onError(e);
   }
};

const getFileUrl = async (identifier: string): Promise<string> => {
   try {
      const resolver = await resolverModulePromise;
      return await resolver.getFileUrl(identifier);
   } catch (e) {
      onError(e);
   }
};

const getFileContentType = async (identifier: string): Promise<string> => {
   try {
      const resolver = await resolverModulePromise;
      return await resolver.getContentType(identifier);
   } catch (e) {
      onError(e);
   }
};

const deleteFile = async (identifier: string): Promise<string> => {
   try {
      const storage = await storageModulePromise;
      return await storage.deleteFile(identifier);
   } catch (e) {
      onError(e);
   }
};

export default {
   login,
   loginAndSave,
   ensureLogInComplete,
   getToken,
   refreshToken,
   getUserInfo,
   upload,
   getFileUrl,
   getFileContentType,
   deleteFile,
   createThumbnail
};
